import { useCallback, useEffect, useRef, useState } from "react";
import { createTheme, Fade, Stack, ThemeProvider } from "@mui/material";
import localStorageUtils from "./hooks/localStorageUtils";
import './i18n';
import createCache from '@emotion/cache';
import { LS_KEY_LANG, LS_KEY_RIDDLE, SENT_VISIT, SENT_STATS, STEPS } from "./consts";
import useDataProvider from "./hooks/useDataProvider";
import useLangChanger from "./hooks/useLangs";
import Steps from "./pages/Steps";
import logo from './assets/svgGame/logo.svg'
import footerBg from './assets/svgGame/footer_bg.svg'
import Backgrounds from "./components/Background";
import { CacheProvider } from "@emotion/react";
import stylisRTLPlugin from "stylis-plugin-rtl";
import LanguageSwitcher from "./components/LanguageSwitcher";
import playerTokenUtils from "./utils/playerTokenUtils";
import useSignalR from "./hooks/useSignalR";

function App() {
  const { currLang, changeLanguage } = useLangChanger();
  const [step, setStep] = useState(STEPS.HOME);
  const [index, setIndex] = useState(0);
  const [riddle, setRiddle] = useState();
  const [showTransition, setShowTransition] = useState(true);
  const { getRiddle, riddleCount } = useDataProvider();
  const { initSignalR, connectSignalR, disconnectSignalR, sendToSignalR } = useSignalR()

  // const { handleCollectItem } = useCollection();

  const basketRef = useRef(null);

  const token = playerTokenUtils.getPlayerToken();

  const itemRefs = useRef({
    mustard: null,
    bottle: null,
    glove: null,
    shoe: null,
    tin: null,
    glass: false,
    canned: false
  });

  const setItemRef = useCallback((itemName, element) => {
    if (!element) return;
    itemRefs.current[itemName] = element;
  }, []);

  useEffect(() => {
    if (index < riddleCount)
      setRiddle(getRiddle(index));
  }, [currLang, index, riddleCount]);

  useEffect(() => {
    const lang = localStorageUtils.getItem(LS_KEY_LANG) || { code: 'he', isRtl: true };
    changeLanguage(lang)
    var riddleStorage = localStorageUtils.getItem(LS_KEY_RIDDLE) ?? 0;

    initSignalR('https://apicoralworldgame.cdigital.co.il/clientSiteHub');
    // initSignalR('https://192.168.2.2:45480/clientSiteHub');
    connectSignalR().then(() => {

      var sentVisitStat = localStorageUtils.getItem(SENT_VISIT) ?? false;

      if (!sentVisitStat) {
        sendAddStat(-1, true); // Send initial visit stat
        localStorageUtils.setItem(SENT_VISIT, -1); // Mark that visit stat has been sent
      }

    }).catch((error) => {
      console.error('SignalR connection error:', error);
    });

    // if (riddleStorage > riddleCount - 1)
    //   riddleStorage = riddleCount - 1;

    setIndex(riddleStorage);

    if (riddleStorage) {
      if (riddleStorage < riddleCount)
        setStep(STEPS.RIDDLE);
      else
        setStep(STEPS.FINISH);
    }

    Object.values(itemRefs.current).forEach((item, idx) => {
      if (riddleStorage > 0 && idx <= riddleStorage - 1)
        handleCollectItem(idx)
    })

    // Cleanup SignalR connection on component unmount
    return () => {
      disconnectSignalR();
    };

  }, []);

  const theme = createTheme({
    direction: currLang.isRtl ? 'rtl' : 'ltr',
    typography: {
      fontFamily: 'Assistant, Arial, sans-serif',
    },
    palette: {
      primary: {
        main: "#FF5733",
        contrastText: "#fff",
      },
      secondary: {
        main: "#33A1FF",
      },
      textFrame: {
        main: "#683119",
      },
    },
    components: {
      MuiButton: {
        defaultProps: {
          variant: "outlined",
        },
      },
    },
  });

  const handleCollectItem = async (idx) => {
    const itemImg = Object.values(itemRefs.current)[idx];

    if (itemImg) {
      requestAnimationFrame(() => {
        calculateAndApplyPosition(itemImg);
        itemImg.style.transform = `translate(var(--translate-x), var(--translate-y)) rotate(180deg) scale(0.9)`;
        itemImg.style.pointerEvents = 'none';
      });
    }

    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  const calculateAndApplyPosition = useCallback((itemElement) => {
    if (!basketRef.current || !itemElement) return;

    const basketRect = basketRef.current.getBoundingClientRect();
    const itemRect = itemElement.getBoundingClientRect();

    const translateX = basketRect.left - itemRect.left + (basketRect.width / 2) - 45;
    const translateY = basketRect.top - itemRect.top + (basketRect.height / 2) - 25;

    itemElement.style.setProperty('--translate-x', `${translateX}px`);
    itemElement.style.setProperty('--translate-y', `${translateY}px`);
  }, []);

  // Function to send AddStat to SignalR
  const sendAddStat = useCallback(async (questionId, answeredCorrect) => {
    if (!questionId) {
      console.warn('No question ID provided for AddStat');
      return;
    }

    // Get the array of sent stats from local storage
    const sentStats = localStorageUtils.getItem(SENT_STATS) || [];

    // Check if this questionId has already been sent
    if (sentStats.includes(questionId)) {
      console.log(`Question ${questionId} already sent, skipping`);
      return;
    }

    try {
      sendToSignalR("AddStat", {
        questionId: questionId,
        playerToken: token,
        answeredCorrect: answeredCorrect
      });

      // Add the questionId to the sent stats array and save to local storage
      sentStats.push(questionId);
      localStorageUtils.setItem(SENT_STATS, sentStats);

    } catch (error) {
      console.error('Error sending AddStat:', error);
    }
  }, []);

  const handleNext = () => {
    setShowTransition(false);

    const nextIdx = step === STEPS.HOME ? 0 : index + 1
    setIndex(nextIdx);

    setTimeout(async () => {
      if (index < riddleCount - 1) {

        if (nextIdx > 0)
          await handleCollectItem(nextIdx - 1);

        setStep(STEPS.RIDDLE);
        setRiddle(getRiddle(nextIdx));
      }
      else {
        await handleCollectItem(nextIdx - 1);
        setStep(STEPS.FINISH);
        sendAddStat(100, true);
      }

      localStorageUtils.setItem(LS_KEY_RIDDLE, nextIdx);

      setShowTransition(true);
    }, 300);
  };

  const finishedRiddle = () => {
    setShowTransition(false);

    // Send AddStat when riddle is completed
    if (riddle && riddle.id)
      sendAddStat(riddle.id, true);

    setTimeout(() => {
      setStep(STEPS.QUESTION);
      setShowTransition(true);
    }, 500);
  };

  // document.body.dir = 'rtl';
  const emotionCacheOptions = {
    rtl: {
      key: 'muirtl',
      stylisPlugins: [stylisRTLPlugin],
      prepend: true,
    },
    ltr: {
      key: 'muiltr',
      stylisPlugins: [],
      prepend: true,
    },
  };

  return (
    <CacheProvider value={createCache(emotionCacheOptions[currLang.isRtl ? 'rtl' : 'ltr'])}>
      <ThemeProvider theme={theme}>
        <Stack
          position='absolute'
          top={0}
          left={0}
          width='100vw'
          height='100vh'
          justifyContent='center'
          overflow='hidden'
          zIndex={80}
          sx={{
            // ?          backgroundImage: `url(${frame_game})`,
            backgroundSize: 'cover',
          }}
        >
          <LanguageSwitcher />

          <Backgrounds basketRef={basketRef} setItemRef={setItemRef} />

          <img
            src={footerBg}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: "100%",
              objectFit: 'cover',
              zIndex: 10
            }}
          />

          <Stack
            position='absolute'
            alignItems='center'
            top={10}
            width={'100%'}
          >
            <img
              src={logo}
              style={{ width: '12rem' }}
            />
          </Stack>

          <Fade in={showTransition} timeout={500}>
            <Stack justifyContent='center' alignItems='center'>
              <Steps
                finishedRiddle={finishedRiddle}
                handleNext={handleNext}
                riddle={riddle}
                step={step}
                sendAddStat={sendAddStat}
              />
            </Stack>
          </Fade>
        </Stack>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;